/**
 * Storefront PREVIEW mode detection.
 *
 * The store-editor embeds the live public storefront (`/store/<slug>`)
 * in a SAME-ORIGIN iframe so the distributor can see what shoppers see.
 * A same-origin iframe shares `localStorage` (and therefore the auth
 * token) with the parent editor. The storefront's normal entry flow
 * performs a shopper "token swap" -- it POSTs `/auth/getToken/<id>` and
 * overwrites `localStorage["token"]` + Vuex `login`. Inside the preview
 * iframe that swap clobbers the parent editor's session (e.g. a
 * superadmin impersonating a distributor), downgrading the editor to a
 * Shopper identity and breaking its subsequent API calls (500/403).
 *
 * To stop the pollution the editor appends `?preview=1` to the iframe
 * `src` ONLY (the open-in-new-tab link stays the plain interactive
 * URL). The storefront reads that flag and SKIPS the token swap / shopper
 * login entirely, touching no shared session state. It renders read-only
 * against whatever session already exists.
 *
 * Single source of truth for the flag so both StorefrontViewShopper and
 * StorefrontSessionGate detect preview mode identically.
 *
 * Behavioural tests: `__tests__/storefront-preview.spec.js`.
 */

export const STOREFRONT_PREVIEW_PARAM = "preview";

/**
 * True when the current route is a read-only storefront preview.
 *
 * Accepts the router `route` object and inspects `route.query.preview`.
 * Vue Router normalises `?preview=1` to the string "1"; a bare
 * `?preview` becomes the empty string. Both count as preview. The flag
 * is absent (undefined) on a normal shopper visit.
 */
export function isStorefrontPreview(route) {
    const value = route?.query?.[STOREFRONT_PREVIEW_PARAM];
    // Present-but-falsey values ("0", "false", "no") explicitly opt out
    // so the flag can never accidentally latch a real shopper into a
    // non-rendering session.
    if (value === undefined || value === null) return false;
    const normalized = String(value).toLowerCase();
    if (normalized === "0" || normalized === "false" || normalized === "no") {
        return false;
    }
    return true;
}

/**
 * True when this page runs inside a frame (the store editor's preview
 * drawer) rather than as its own tab. A cross-origin parent makes reading
 * `top` throw; that is a frame too.
 */
export function isFramedWindow(win = typeof window === "undefined" ? null : window) {
    if (!win) return false;
    try {
        return win.self !== win.top;
    } catch {
        return true;
    }
}

/**
 * True when a path is inside the CANONICAL shopper tree (`/shop/...`) --
 * the only destination the preview flag is allowed to travel to.
 *
 * Deliberately excludes the legacy `/store/...` entry paths. Those are
 * ENTRY urls: they redirect into `/shop/...` and `router/redirects.js`
 * already preserves their query explicitly, so the iframe's own
 * `/store/<slug>?preview=1` src arrives in preview mode without any help
 * from this guard. Propagating INTO `/store/...` as well would mean a
 * freshly-entered legacy URL inherits preview-ness from whatever the
 * router happened to be showing before, which is not the intent and is
 * observable: it rewrote `/store/<slug>/products` to
 * `/shop/<slug>?preview=1` in `storefront-legacy-retirement-p8.spec.js`.
 *
 * Narrow beats clever here -- the flag should only ride FORWARD with an
 * in-preview click, never re-latch onto a new entry.
 */
function isStorefrontPath(path) {
    return typeof path === "string" &&
        (path === "/shop" || path.startsWith("/shop/"));
}

/**
 * Make preview-ness DURABLE for the lifetime of the iframe session.
 *
 * The store-editor preview iframe is interactive: the distributor can
 * click through the storefront the way a shopper would. But every
 * in-storefront navigation target is built as a BARE PATH string --
 * `StorefrontProductGrid.productPath` returns
 * `/shop/<slug>/c/<cat>/p/<id>`, `StorefrontLayout` pushes
 * `/shop/<slug>/cart`, and so on. Vue Router does NOT carry the current
 * query across a push to a bare path, so WITHOUT this guard the very
 * first click would drop `?preview=1` and land the iframe on a NORMAL
 * shopper route.
 *
 * That is not a cosmetic loss. The iframe is same-origin and shares
 * localStorage with the editor, so a non-preview storefront route runs
 * the shopper token swap and overwrites the editor's (impersonation)
 * auth token -- the exact failure `?preview=1` exists to prevent. See
 * the header of this file and StorefrontSessionGate.vue.
 *
 * The guard is deliberately narrow:
 *   - it only ADDS the flag to a destination that is INSIDE the
 *     storefront tree and is arriving FROM a preview route, so it can
 *     never latch a real shopper into preview mode;
 *   - it never overrides a destination that already states its own
 *     `preview` value, so an explicit opt-out (`?preview=0`) latches OFF
 *     rather than being resurrected on the next hop;
 *   - it leaves the query otherwise untouched, so a caller's own params
 *     (sort, filters) survive.
 *
 * Idempotent: wiring it twice on the same router installs one guard.
 *
 * Note this is a same-document mechanism. A FULL page load inside the
 * iframe (rather than a router navigation) starts from the URL the
 * browser requests; that URL still carries the flag because the guard
 * rewrote it into the address bar on the way in. The privileged-session
 * check (router/privilegedSession.js) remains the independent second
 * line of defence for a back-office / impersonation session either way.
 *
 * Behavioural tests:
 * `components/Storefront/__tests__/storefront-preview-durability.spec.js`.
 */
const INSTALLED = new WeakSet();

export function installPreviewQueryPersistence(router) {
    if (!router || typeof router.beforeEach !== "function") return;
    if (INSTALLED.has(router)) return;
    INSTALLED.add(router);

    router.beforeEach((to, from) => {
        // The destination already decided for itself (including an
        // explicit `preview=0` opt-out) -- never second-guess it.
        if (to.query?.[STOREFRONT_PREVIEW_PARAM] !== undefined) return true;
        // Only propagate FROM a preview route...
        if (!isStorefrontPreview(from)) return true;
        // ...and only INTO the canonical shopper tree.
        if (!isStorefrontPath(to.path)) return true;
        // ...and only for a DIRECT navigation.
        //
        // `redirectedFrom` is set when this navigation arrived through a
        // redirect record -- i.e. someone entered a legacy `/store/...`
        // URL, which router/redirects.js forwards into `/shop/...`
        // (preserving that URL's OWN query). That is a fresh ENTRY, not a
        // click inside the preview, and it must not inherit preview-ness
        // from whatever the router happened to be showing before. A real
        // in-preview click has no `redirectedFrom` (measured: a direct
        // push reports NONE, the legacy hop reports `/store/<slug>/...`).
        //
        // Without this check the guard cannot tell the two apart, because
        // the redirect resolves BEFORE the guard runs -- both arrive as
        // `from=/shop/<slug>?preview=1` -> `to=/shop/<slug>`.
        if (to.redirectedFrom) return true;

        // Redirect by PATH, never by `name` + `params`.
        //
        // `to` is a resolved route, so spreading it carries BOTH `path` and
        // `name`/`params`. Vue Router prefers name+params when both are
        // present and re-STRINGIFIES the path from those params -- and its
        // stringifier rejects an empty-string value for any param not
        // declared `optional`, throwing `Missing required param`. That throw
        // rejects the navigation, so the click silently does nothing.
        //
        // The storefront HOME grid hits this on every product card: Home owns
        // no category segment, so `StorefrontProductGrid.productPath` builds
        // `/shop/<slug>/c//p/<id>` with an EMPTY `categorySlug`. The route
        // MATCHES that url -- `c/:categorySlug([^/]*)/p/:id` parses it fine --
        // but the custom regex does not make the param optional, so building
        // the same url BACK from params throws. Parsing and stringifying are
        // separate directions and only the parsing one was fixed.
        //
        // Forwarding `path` (which is already the concrete, resolved url)
        // keeps the stringifier out of the loop entirely and is correct for
        // every route shape, not just this one.
        return {
            path: to.path,
            hash: to.hash,
            query: {
                ...to.query,
                [STOREFRONT_PREVIEW_PARAM]:
                    from.query[STOREFRONT_PREVIEW_PARAM],
            },
        };
    });
}

export default isStorefrontPreview;
